import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../../components/ui/Button';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { Skeleton } from 'primereact/skeleton';

import { useGetWallMediaLibrary } from '../../../../queries/videoWall';
import useEditorDirty from '../useEditorDirty';

// Persisted item shape — shared by save() and the dirty serializer so Save enables
// only when the items actually change.
const serializeItems = (items) => JSON.stringify(items.map((it) => ({
  content_type: it.content_type,
  fit_mode: it.fit_mode || 'letterbox',
  media_file_id: it.content_type === 'split' ? null : (it.media_file_id || null),
  file_url: it.content_type === 'split' ? null : (it.file_url || null),
  duration_seconds: it.duration_seconds || 1,
  screen_assignments: it.content_type === 'split' ? (it.screen_assignments || {}) : null,
})));

/**
 * Shared content editor for a wall's single playlist. Extracted from Show.jsx so
 * the create wizard can reuse the exact same editing UX (P4). Pure presentation +
 * an onSave callback — the parent owns the create/update mutation. Sync-safe: it
 * only builds the playlist payload, never touches the playback/sync path.
 */

export const CONTENT_TYPES = ['full_wall', 'split', 'media_file'];
// How the source video is fitted to the wall canvas before per-cell splitting.
export const FIT_MODES = ['letterbox', 'crop', 'stretch'];

// Format seconds → "m:ss" for the read-only video duration label.
const fmtDuration = (secs) => {
  const s = Math.max(0, Math.round(secs || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/* ───────────── Media picker dialog ───────────── */
export function MediaPickerDialog({ visible, onHide, onPick }) {
  const { t } = useTranslation();
  const { data, isLoading } = useGetWallMediaLibrary(visible);
  const items = useMemo(() => {
    const arr = data?.data ?? data ?? [];
    return Array.isArray(arr) ? arr : [];
  }, [data]);

  return (
    <Dialog header={<span>🎬 {t('video_wall.pick_media')}</span>} visible={visible} onHide={onHide}
      style={{ width: 'min(95vw, 680px)' }} modal>
      {isLoading ? (
        <div className="row g-2">{[...Array(8)].map((_, i) => (
          <div key={i} className="col-4"><Skeleton height="7rem" /></div>))}</div>
      ) : items.length === 0 ? (
        <div className="p-4 text-center text-muted small">{t('video_wall.no_media')}</div>
      ) : (
        <div className="row g-2">
          {items.map((m) => (
            <div key={m.id} className="col-6 col-md-4 col-lg-3">
              <button type="button" className="p-0 border-0 w-100"
                style={{ background: 'transparent', cursor: 'pointer' }}
                onClick={() => { onPick(m); onHide(); }} title={m.file_name}>
                <img src={m.thumb || m.path} alt={m.file_name} className="w-100"
                  style={{ height: 100, objectFit: 'cover', borderRadius: 10, border: '1px solid var(--pp-border)' }} />
                <small className="d-block text-truncate" style={{ color: 'var(--pp-text-secondary)' }}>
                  {m.type === 'VIDEO' ? '🎬 ' : '🖼️ '}{m.file_name}
                </small>
              </button>
            </div>
          ))}
        </div>
      )}
    </Dialog>
  );
}

/* ───────────── Playlist editor dialog ───────────── */
export default function PlaylistEditor({ visible, onHide, wall, playlist, onSave, saving }) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [picker, setPicker] = useState(null); // { itemIdx } or { itemIdx, position } for split
  const { markClean, isDirty } = useEditorDirty();

  // Resolve already-saved media_file_id → type/duration so we can lock the duration
  // field for video items (the wall plays a video's REAL file length; the slot is
  // ignored). Same library the picker uses → react-query dedupes the request.
  const { data: mediaRaw } = useGetWallMediaLibrary(visible);
  const mediaMap = useMemo(() => {
    const arr = mediaRaw?.data ?? mediaRaw ?? [];
    const m = {};
    (Array.isArray(arr) ? arr : []).forEach((x) => { m[x.id] = x; });
    return m;
  }, [mediaRaw]);
  // Real seconds when the item is a video whose length we know (else null = editable;
  // a raw pasted file_url video stays editable, length unknown).
  const lockedVideoSecs = (it) => {
    if (it.content_type !== 'full_wall' && it.content_type !== 'media_file') return null;
    const md = it.media_file_id ? mediaMap[it.media_file_id] : null;
    return md && md.type === 'VIDEO' && md.duration > 0 ? md.duration : null;
  };

  React.useEffect(() => {
    if (visible) {
      const its = (playlist?.items || []).map((it) => ({
        content_type: it.content_type,
        fit_mode: it.fit_mode || 'letterbox',
        media_file_id: it.media_file_id || null,
        media_label: it.media_file_id ? t('video_wall.media_selected') : (it.file_url || ''),
        file_url: it.file_url || null,
        duration_seconds: it.duration_seconds || 10,
        screen_assignments: it.screen_assignments || {},
      }));
      setItems(its);
      markClean(serializeItems(its)); // freshly opened == clean
    }
  }, [visible, playlist]); // eslint-disable-line react-hooks/exhaustive-deps

  const cellCount = (wall?.rows || 1) * (wall?.columns || 1);

  const addItem = () => setItems((arr) => [...arr, {
    content_type: 'full_wall', fit_mode: 'letterbox', media_file_id: null, media_label: '', file_url: null,
    duration_seconds: 10, screen_assignments: {},
  }]);
  const removeItem = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));
  const move = (idx, dir) => setItems((arr) => {
    const next = [...arr];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return arr;
    [next[idx], next[j]] = [next[j], next[idx]];
    return next;
  });
  const patch = (idx, p) => setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, ...p } : it)));

  const onPickMedia = (m) => {
    if (!picker) return;
    if (picker.position != null) {
      patch(picker.itemIdx, {
        screen_assignments: { ...items[picker.itemIdx].screen_assignments, [picker.position]: m.id },
      });
    } else {
      // Default the slot to the VIDEO's real length (media.duration is seconds);
      // images keep their existing default. The user can still adjust it after.
      const vidDur = m.type === 'VIDEO' && m.duration > 0 ? { duration_seconds: Math.max(1, Math.round(m.duration)) } : {};
      patch(picker.itemIdx, { media_file_id: m.id, file_url: null, media_label: m.file_name, ...vidDur });
    }
    setPicker(null);
  };

  const serialized = serializeItems(items);
  const dirty = isDirty(serialized);

  const save = () => {
    const payload = {
      // Single-playlist UX: the wall's one playlist is always active, so Start and
      // Refresh resolve the SAME playlist (fixes B1). Name is kept hidden/defaulted.
      name: (playlist?.name || t('video_wall.default_playlist_name')),
      is_active: true,
      items: items.map((it) => ({
        content_type: it.content_type,
        fit_mode: it.fit_mode || 'letterbox',
        media_file_id: it.content_type === 'split' ? null : it.media_file_id,
        file_url: it.content_type === 'split' ? null : it.file_url,
        duration_seconds: it.duration_seconds,
        screen_assignments: it.content_type === 'split' ? it.screen_assignments : null,
      })),
    };
    onSave(payload);
  };

  return (
    <Dialog header={t('video_wall.edit_content')}
      visible={visible} onHide={onHide} style={{ width: 'min(96vw, 720px)' }} modal>
      {items.map((it, idx) => (
        <div key={idx} className="p-3 mb-2" style={{ background: 'var(--pp-bg-secondary)', borderRadius: 'var(--pp-radius-md)', border: '1px solid var(--pp-border)' }}>
          <div className="d-flex align-items-center justify-content-between mb-2">
            <strong style={{ color: 'var(--pp-text-primary)' }}>#{idx + 1}</strong>
            <div className="d-flex gap-1">
              <Button icon="pi pi-arrow-up" text rounded size="small" onClick={() => move(idx, -1)} disabled={idx === 0} />
              <Button icon="pi pi-arrow-down" text rounded size="small" onClick={() => move(idx, 1)} disabled={idx === items.length - 1} />
              <Button icon="pi pi-trash" text rounded size="small" severity="danger" onClick={() => removeItem(idx)} />
            </div>
          </div>
          <div className="row g-2">
            <div className="col-12 col-md-5">
              <label className="form-label small" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.content_type')}</label>
              <Dropdown value={it.content_type}
                options={CONTENT_TYPES.map((c) => ({ label: t(`video_wall.ct_${c}`), value: c }))}
                onChange={(e) => patch(idx, { content_type: e.value })} className="w-100" />
            </div>
            <div className="col-6 col-md-4">
              <label className="form-label small" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.duration_seconds')}</label>
              {(() => {
                const locked = lockedVideoSecs(it);
                return locked != null ? (
                  <div className="d-flex align-items-center gap-2 px-2" title={t('video_wall.library.video_duration_locked')}
                    style={{ height: 42, border: '1px solid var(--pp-border)', borderRadius: 'var(--pp-radius-sm)',
                      background: 'var(--pp-bg-tertiary, var(--pp-bg-secondary))', color: 'var(--pp-text-secondary)' }}>
                    <i className="pi pi-lock" style={{ fontSize: 12 }} />
                    <span>{fmtDuration(locked)}</span>
                    <i className="pi pi-info-circle ms-auto" style={{ fontSize: 12 }}
                      title={t('video_wall.library.video_duration_locked')} />
                  </div>
                ) : (
                  <InputNumber value={it.duration_seconds} min={1} max={86400} showButtons
                    onValueChange={(e) => patch(idx, { duration_seconds: e.value || 1 })} className="w-100" />
                );
              })()}
            </div>
          </div>

          {it.content_type === 'full_wall' && (
            <div className="mt-2">
              <label className="form-label small" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.fit_mode')}</label>
              <Dropdown value={it.fit_mode || 'letterbox'}
                options={FIT_MODES.map((f) => ({ label: t(`video_wall.fit_${f}`), value: f }))}
                onChange={(e) => patch(idx, { fit_mode: e.value })} className="w-100" />
              <small className="d-block mt-1" style={{ color: 'var(--pp-text-secondary)' }}>
                {t(`video_wall.fit_${it.fit_mode || 'letterbox'}_help`)}
              </small>
            </div>
          )}

          {it.content_type !== 'split' ? (
            <div className="mt-2 d-flex align-items-center gap-2 flex-wrap">
              <Button label={t('video_wall.choose_media')} icon="pi pi-images" size="small" outlined
                onClick={() => setPicker({ itemIdx: idx })} />
              <span style={{ color: 'var(--pp-text-secondary)' }} className="small text-truncate">
                {it.media_label || t('video_wall.or_paste_url')}
              </span>
              <InputText value={it.file_url || ''} placeholder="https://…"
                onChange={(e) => patch(idx, { file_url: e.target.value || null, media_file_id: null, media_label: e.target.value })}
                className="flex-grow-1" style={{ minWidth: 160 }} />
            </div>
          ) : (
            <div className="mt-2">
              <small style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.split_help')}</small>
              <div className="d-flex flex-wrap gap-2 mt-1">
                {Array.from({ length: cellCount }).map((_, i) => {
                  const pos = i + 1;
                  const assigned = it.screen_assignments?.[pos];
                  return (
                    <Button key={pos} size="small"
                      outlined={!assigned}
                      label={`${pos}${assigned ? ' ✓' : ''}`}
                      onClick={() => setPicker({ itemIdx: idx, position: pos })} />
                  );
                })}
              </div>
            </div>
          )}
        </div>
      ))}

      <Button label={t('video_wall.add_content')} icon="pi pi-plus" text onClick={addItem} className="mb-3" />

      <div className="d-flex justify-content-end gap-2 pt-2" style={{ borderTop: '1px solid var(--pp-border)' }}>
        <Button label={t('video_wall.cancel')} text onClick={onHide} />
        <Button label={t('video_wall.save')} icon="pi pi-check" loading={saving} onClick={save} disabled={!dirty} />
      </div>

      <MediaPickerDialog visible={!!picker} onHide={() => setPicker(null)} onPick={onPickMedia} />
    </Dialog>
  );
}
